import React, { useCallback, useEffect } from 'react';
import { ReactComponent as ClippingIconA } from '../../assets/icons/clipping/clippingA.svg';
import { ReactComponent as ClippingIconB } from '../../assets/icons/clipping/clippingB.svg';
import type { ImagesClippingController } from './ImagesClippingController';
import { SliceImageButton } from '../common/SliceImageButton';
import { observer } from 'mobx-react';
import { useInjection } from 'inversify-react';
import type { ViewsService } from '@/engine/app/Views.service';
import { TYPES } from '@/engine/ioc';
import { type IReactionDisposer, reaction } from 'mobx';

export const ClippingButton: React.FC<{
  descriptor: string;
  controller?: ImagesClippingController;
  isMaximized?: boolean;
}> = observer(({ descriptor, controller, isMaximized }) => {
  const [isEnabled, setEnabled] = React.useState(false);
  const [isFlipped, setFlipped] = React.useState(false);

  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);

  const setStateFromController = (controller: ImagesClippingController): void => {
    const plane = controller.getPlane();
    const flipped = plane ? controller.isFlipped(plane) : false;
    setFlipped(flipped);
    setEnabled(!!plane);
  };

  useEffect(() => {
    if (!controller) return;

    const clippingReactionDisposer: IReactionDisposer = reaction(
      () => Array.from(viewsService.currentViewOrThrow.clippingPlanes.values()).slice(),
      (): void => {
        setStateFromController(controller);
      }
    );

    setStateFromController(controller);
    return (): void => clippingReactionDisposer();
  }, [controller, viewsService.currentViewOrThrow.clippingPlanes]);

  const changeState = useCallback((): void => {
    if (!controller) return;

    if (!isEnabled) {
      controller.setEnabled(true); // Disabled => Clipped
    } else {
      controller.flipPlane();
      if (isFlipped) controller.setEnabled(false); // Flipped => Disabled
    }
  }, [controller, isEnabled, isFlipped]);

  return (
    <SliceImageButton
      className={`clipping-icon ${descriptor}-clippingTool ${isMaximized ? 'maximized' : 'minimized'}`}
      onClick={changeState}
      data-testid={`${descriptor}-clippingBtn`}
      $isMaximized={isMaximized}
      $isActive={isEnabled}
    >
      {isFlipped ? <ClippingIconB style={customSvgStyle} /> : <ClippingIconA style={customSvgStyle} />}
    </SliceImageButton>
  );
});

const customSvgStyle = {
  width: 24,
  height: 24
};
